// Single keys for all whiteboard data and metadata (stored as objects keyed by sessionId)
const WHITEBOARD_DATA_KEY = 'whiteboardData';
const WHITEBOARD_META_KEY = 'whiteboardMeta';
const WHITEBOARD_SESSION_KEY = 'WHITEBOARD_SESSION_ID';

// Legacy keys (for cleanup of old format data)
const WHITEBOARD_STORAGE_PREFIX = 'whiteboardData:';
const WHITEBOARD_METADATA_PREFIX = 'whiteboardMeta:';

// Expiration time: 30 seconds for testing, change to 12 * 60 * 60 * 1000 (12 hours) for production
const WHITEBOARD_EXPIRATION_TIME = 12 * 60 * 60 * 1000; // 12 hours (for testing)
// const WHITEBOARD_EXPIRATION_TIME = 12 * 60 * 60 * 1000; // 12 hours (production)

/**
 * NEW STORAGE STRUCTURE (Single key for all sessions):
 *
 * localStorage["whiteboardData"] = {
 *   "sessionId1": "base64CanvasData...",
 *   "sessionId2": "base64CanvasData...",
 *   "sessionId3": "base64CanvasData..."
 * }
 *
 * localStorage["whiteboardMeta"] = {
 *   "sessionId1": { sessionId, timestamp, expiresAt },
 *   "sessionId2": { sessionId, timestamp, expiresAt },
 *   "sessionId3": { sessionId, timestamp, expiresAt }
 * }
 */

/**
 * IMPORTANT: Multi-User Cleanup Behavior
 * 
 * Each user's browser stores whiteboard data in their OWN localStorage with their OWN timestamp.
 * When socket broadcasts whiteboard updates:
 *   1. User A draws → broadcasts to all users via socket
 *   2. User B receives → saves to THEIR localStorage with THEIR current timestamp (Date.now())
 *   3. User C receives → saves to THEIR localStorage with THEIR current timestamp
 * 
 * Cleanup happens INDEPENDENTLY on each user's browser:
 *   - User A: Data expires 12 hours after THEY saved it
 *   - User B: Data expires 12 hours after THEY received and saved it
 *   - User C: Data expires 12 hours after THEY received and saved it
 * 
 * This works across all scenarios:
 *   ✓ Browser closed/reopened
 *   ✓ Tab closed/reopened
 *   ✓ System restart
 *   ✓ Days/weeks later
 * 
 * Each user's cleanup timer runs independently in their own browser.
 * 
 * EXAMPLE:
 *   10:00 AM - Admin draws on whiteboard
 *   10:00:01 AM - User 1 receives via socket, saves with timestamp=10:00:01
 *   10:00:03 AM - User 2 receives via socket, saves with timestamp=10:00:03
 *   
 *   User 1's browser: Data expires at 10:00:01 + 12 hours = 10:00 PM
 *   User 2's browser: Data expires at 10:00:03 + 12 hours = 10:00 PM (2 sec later)
 *   
 *   Each user's cleanup happens in their own browser independently.
 */

// Cleanup interval timer
let cleanupIntervalId = null;
const CLEANUP_CHECK_INTERVAL = 5 * 1000; // Check every 5 seconds

// Callback listeners for when data expires
let expirationCallbacks = [];

const isStorageAvailable = () =>
    typeof window !== 'undefined' && window.localStorage && window.sessionStorage;

const toSessionId = (visitId) => {
    if (visitId === null || visitId === undefined || visitId === '') {
        return '';
    }
    return String(visitId);
};

const getActiveSessionId = () => {
    if (!isStorageAvailable()) {
        return '';
    }
    return toSessionId(window.sessionStorage.getItem(WHITEBOARD_SESSION_KEY));
};

export const getWhiteboardStorageKey = (visitId) => {
    // Kept for backward compatibility - returns the single data key
    return WHITEBOARD_DATA_KEY;
};

export const getWhiteboardMetadataKey = (visitId) => {
    // Kept for backward compatibility - returns the single metadata key
    return WHITEBOARD_META_KEY;
};

// Read all whiteboard data as object { sessionId: canvasData, ... }
const getAllWhiteboardData = () => {
    if (!isStorageAvailable()) return {};
    try {
        const raw = window.localStorage.getItem(WHITEBOARD_DATA_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        // If old format (plain string), return empty object - legacy data will be migrated/ignored
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch (e) {
        return {};
    }
};

// Read all whiteboard metadata as object { sessionId: {...}, ... }
const getAllWhiteboardMeta = () => {
    if (!isStorageAvailable()) return {};
    try {
        const raw = window.localStorage.getItem(WHITEBOARD_META_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch (e) {
        return {};
    }
};

// Write whiteboard data object back to localStorage
const writeAllWhiteboardData = (dataObj) => {
    if (!isStorageAvailable()) return;
    if (Object.keys(dataObj).length === 0) {
        window.localStorage.removeItem(WHITEBOARD_DATA_KEY);
    } else {
        window.localStorage.setItem(WHITEBOARD_DATA_KEY, JSON.stringify(dataObj));
    }
};

// Write whiteboard metadata object back to localStorage
const writeAllWhiteboardMeta = (metaObj) => {
    if (!isStorageAvailable()) return;
    if (Object.keys(metaObj).length === 0) {
        window.localStorage.removeItem(WHITEBOARD_META_KEY);
    } else {
        window.localStorage.setItem(WHITEBOARD_META_KEY, JSON.stringify(metaObj));
    }
};

// Check if whiteboard data has expired for a specific session
const isSessionExpired = (metadata) => {
    if (!metadata || !metadata.timestamp) return false;
    const expirationTime = metadata.timestamp + WHITEBOARD_EXPIRATION_TIME;
    return Date.now() > expirationTime;
};

// Clean up legacy per-session keys (old storage format)
const cleanupLegacyKeys = () => {
    if (!isStorageAvailable()) return;
    try {
        const keys = Object.keys(window.localStorage);
        keys.forEach((key) => {
            if (
                (key.startsWith(WHITEBOARD_STORAGE_PREFIX) && key !== WHITEBOARD_DATA_KEY) ||
                (key.startsWith(WHITEBOARD_METADATA_PREFIX) && key !== WHITEBOARD_META_KEY)
            ) {
                window.localStorage.removeItem(key);
            }
        });
    } catch (e) {
        // ignore
    }
};

// Clean up expired whiteboard data
// This runs in each user's browser independently
export const cleanupExpiredWhiteboardData = () => {
    if (!isStorageAvailable()) {
        return;
    }

    try {
        // Clean up any old-format keys
        cleanupLegacyKeys();

        const allData = getAllWhiteboardData();
        const allMeta = getAllWhiteboardMeta();
        const expiredSessions = [];

        Object.keys(allMeta).forEach((sessionId) => {
            const metadata = allMeta[sessionId];
            if (isSessionExpired(metadata)) {
                // const savedAt = new Date(metadata.timestamp).toISOString();
                // const expiredAt = new Date(metadata.expiresAt).toISOString();
                // console.log(
                //     `[This User's Browser] Removing expired whiteboard data:\n` +
                //         `  Session ID: ${sessionId}\n` +
                //         `  Saved at: ${savedAt}\n` +
                //         `  Expired at: ${expiredAt}`
                // );
                delete allData[sessionId];
                delete allMeta[sessionId];
                expiredSessions.push(sessionId);
            }
        });

        if (expiredSessions.length > 0) {
            writeAllWhiteboardData(allData);
            writeAllWhiteboardMeta(allMeta);

            // console.log(
            //     `[This User's Browser] Cleanup complete: Removed ${expiredSessions.length} expired whiteboard session(s)`
            // );

            // Notify all registered callbacks about expired sessions
            expiredSessions.forEach((sessionId) => {
                expirationCallbacks.forEach((callback) => {
                    try {
                        callback(sessionId);
                    } catch (error) {
                        console.error('Error in expiration callback:', error);
                    }
                });
            });
        }
    } catch (error) {
        console.error('Error cleaning up expired whiteboard data:', error);
    }
};

export const saveWhiteboardDataForSession = (visitId, canvasData) => {
    if (!isStorageAvailable()) {
        return;
    }

    const sessionId = toSessionId(visitId) || getActiveSessionId();
    if (!sessionId) {
        return;
    }

    // Clean up any expired data before saving
    cleanupExpiredWhiteboardData();

    const allData = getAllWhiteboardData();
    const allMeta = getAllWhiteboardMeta();

    if (canvasData) {
        // Save canvas data under sessionId key
        allData[sessionId] = canvasData;

        // Save/update metadata under sessionId key
        // IMPORTANT: Each user saves with THEIR OWN timestamp (Date.now())
        allMeta[sessionId] = {
            sessionId,
            timestamp: Date.now(),
            expiresAt: Date.now() + WHITEBOARD_EXPIRATION_TIME,
        };

        writeAllWhiteboardData(allData);
        writeAllWhiteboardMeta(allMeta);

        // console.log(
        //     `[This User's Browser] Whiteboard data saved:\n` +
        //         `  Session: ${sessionId}\n` +
        //         `  Saved at: ${new Date(allMeta[sessionId].timestamp).toISOString()}\n` +
        //         `  Expires at: ${new Date(allMeta[sessionId].expiresAt).toISOString()}`
        // );
    } else {
        // Remove this session's data
        delete allData[sessionId];
        delete allMeta[sessionId];
        writeAllWhiteboardData(allData);
        writeAllWhiteboardMeta(allMeta);
    }
};

export const getWhiteboardDataForSession = (visitId) => {
    if (!isStorageAvailable()) {
        return '';
    }

    // Always clean up expired data when accessing whiteboard data
    cleanupExpiredWhiteboardData();

    const sessionId = toSessionId(visitId) || getActiveSessionId();
    if (!sessionId) return '';

    const allMeta = getAllWhiteboardMeta();
    const metadata = allMeta[sessionId];

    // Check if data has expired
    if (metadata && isSessionExpired(metadata)) {
        // console.log(`[This User's Browser] Whiteboard data expired for session ${sessionId}`);
        clearWhiteboardDataForSession(sessionId);
        return '';
    }

    const allData = getAllWhiteboardData();
    return allData[sessionId] || '';
};

export const clearWhiteboardDataForSession = (visitId) => {
    if (!isStorageAvailable()) {
        return;
    }

    const sessionId = toSessionId(visitId);
    if (!sessionId) {
        return;
    }

    const allData = getAllWhiteboardData();
    const allMeta = getAllWhiteboardMeta();

    delete allData[sessionId];
    delete allMeta[sessionId];

    writeAllWhiteboardData(allData);
    writeAllWhiteboardMeta(allMeta);

    if (window.sessionStorage.getItem(WHITEBOARD_SESSION_KEY) === sessionId) {
        window.sessionStorage.removeItem(WHITEBOARD_SESSION_KEY);
    }

    // console.log(`[This User's Browser] Whiteboard data cleared for session ${sessionId}`);
};

export const setActiveWhiteboardSession = (visitId) => {
    if (!isStorageAvailable()) {
        return;
    }

    const nextSessionId = toSessionId(visitId);
    if (!nextSessionId) {
        return;
    }

    window.sessionStorage.setItem(WHITEBOARD_SESSION_KEY, nextSessionId);
};

export const pruneWhiteboardSessionsExcept = (visitId) => {
    if (!isStorageAvailable()) {
        return;
    }

    // Clean up legacy keys and expired data
    cleanupLegacyKeys();
    cleanupExpiredWhiteboardData();
};

// Start automatic cleanup timer (should be called once when app initializes)
export const startWhiteboardCleanupTimer = () => {
    if (cleanupIntervalId) {
        return; // Timer already running
    }

    cleanupIntervalId = setInterval(() => {
        cleanupExpiredWhiteboardData();
    }, CLEANUP_CHECK_INTERVAL);

    // console.log('[This User\'s Browser] Whiteboard cleanup timer started - checking every 5 seconds');
};

// Stop automatic cleanup timer
export const stopWhiteboardCleanupTimer = () => {
    if (cleanupIntervalId) {
        clearInterval(cleanupIntervalId);
        cleanupIntervalId = null;
        // console.log('[This User\'s Browser] Whiteboard cleanup timer stopped');
    }
};

// Initialize whiteboard cleanup system - call this once on app startup
// This runs independently in each user's browser
export const initializeWhiteboardCleanup = () => {
    if (!isStorageAvailable()) {
        return;
    }

    // console.log('========================================');
    // console.log('[This User\'s Browser] Initializing whiteboard cleanup system...');
    // console.log('NOTE: Each user has independent cleanup based on their own saved timestamps');
    // console.log('========================================');
    
    // Immediately clean up any expired data from previous sessions
    // This catches data that expired while the browser was closed
    cleanupExpiredWhiteboardData();
    
    // Start the periodic cleanup timer for this user's browser
    startWhiteboardCleanupTimer();
    
    // Also clean up when page is about to unload (optional, for immediate cleanup)
    if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', () => {
            cleanupExpiredWhiteboardData();
        });
    }
    
    // console.log('[This User\'s Browser] Whiteboard cleanup system initialized');
};

// Register a callback to be notified when whiteboard data expires
export const onWhiteboardDataExpired = (callback) => {
    if (typeof callback === 'function') {
        expirationCallbacks.push(callback);
        return () => {
            // Return unsubscribe function
            expirationCallbacks = expirationCallbacks.filter(cb => cb !== callback);
        };
    }
};

// Remove all expiration callbacks (useful for cleanup)
export const clearExpirationCallbacks = () => {
    expirationCallbacks = [];
};
